import { useState, useEffect } from 'react';
import './QuoteDetail.css';
import { apiFetch } from '../../utils/api';

const STATUS_COLORS = {
  new:         { bg: 'rgba(255,183,206,0.12)', border: 'rgba(255,183,206,0.35)', text: '#FFB7CE' },
  contacted:   { bg: 'rgba(100,180,255,0.12)', border: 'rgba(100,180,255,0.35)', text: '#64b4ff' },
  in_progress: { bg: 'rgba(120,220,160,0.12)', border: 'rgba(120,220,160,0.35)', text: '#78dca0' },
  closed:      { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)', text: 'rgba(255,255,255,0.35)' },
}

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', in_progress: 'In Progress', closed: 'Closed',
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function initials(name) {
  return name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

export default function QuoteDetail({ quote, onClose, onStatusChange }) {
  const [status, setStatus] = useState(quote.status)
  const [saving, setSaving] = useState(false)
  const [booking, setBooking] = useState(null)

  useEffect(() => {
    apiFetch('/api/admin/bookings')
      .then(r => r.json())
      .then(d => {
        const found = (d.bookings || []).find(b => b.quote_id === quote.id)
        if (found) setBooking(found)
      })
      .catch(() => {})
  }, [quote.id])

  const changeStatus = async (newStatus) => {
    if (newStatus === status) return
    setSaving(true)
    try {
      await apiFetch(`/api/quotes/${quote.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      })
      setStatus(newStatus)
      onStatusChange(quote.id, newStatus)
    } catch { /* silent fail */ }
    finally { setSaving(false) }
  }

  return (
    <div className='qd-overlay' onClick={onClose}>
      <div className='qd-panel' onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className='qd-header'>
          <div className='qd-avatar'>{initials(quote.name)}</div>
          <div className='qd-header-info'>
            <h2>{quote.name}</h2>
            <span>{quote.company || 'No company listed'}</span>
          </div>
          <button className='qd-close' onClick={onClose} aria-label='Close'>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className='qd-body'>

          {/* Contact */}
          <div className='qd-section'>
            <p className='qd-label'>Contact</p>
            <div className='qd-contact-row'>
              <a href={`mailto:${quote.email}`} className='qd-contact-chip'>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,4 12,13 2,4"/></svg>
                {quote.email}
              </a>
              <a href={`tel:${quote.phone}`} className='qd-contact-chip'>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.59a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                {quote.phone}
              </a>
            </div>
          </div>

          {/* Status */}
          <div className='qd-section'>
            <p className='qd-label'>Status</p>
            <div className='qd-status-row'>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  className='qd-status-btn'
                  style={status === key ? {
                    background: STATUS_COLORS[key].bg,
                    borderColor: STATUS_COLORS[key].border,
                    color: STATUS_COLORS[key].text,
                  } : {}}
                  onClick={() => changeStatus(key)}
                  disabled={saving}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className='qd-section'>
            <p className='qd-label'>Services Requested</p>
            <div className='qd-chips'>
              {(quote.services || '').split(',').map(s => s.trim()).filter(Boolean).map(s => (
                <span key={s} className='qd-chip'>{s}</span>
              ))}
            </div>
          </div>

          {/* Problem */}
          <div className='qd-section'>
            <p className='qd-label'>Problem to Solve</p>
            <p className='qd-text'>{quote.requirement}</p>
          </div>

          {/* Company desc */}
          {quote.company_desc && (
            <div className='qd-section'>
              <p className='qd-label'>About Their Company</p>
              <p className='qd-text'>{quote.company_desc}</p>
            </div>
          )}

          {/* Budget + Timeline */}
          <div className='qd-section qd-two-col'>
            <div>
              <p className='qd-label'>Budget</p>
              <p className='qd-mono'>{quote.budget}</p>
            </div>
            <div>
              <p className='qd-label'>Timeline</p>
              <p className={`qd-mono ${quote.timeline?.includes('Rush') ? 'qd-mono--warning' : ''}`}>
                {quote.timeline}
              </p>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div className='qd-section'>
              <p className='qd-label'>Additional Notes</p>
              <p className='qd-text'>{quote.notes}</p>
            </div>
          )}

          {/* Meta */}

          {/* Booked Session */}
          <div className='qd-section'>
            <p className='qd-label'>Discovery Session</p>
            {booking ? (
              <div className='qd-booking'>
                <span className='qd-booking-icon'>📅</span>
                <div>
                  <p className='qd-booking-date'>{booking.booking_date} at {booking.booking_time}</p>
                  {booking.notes && <p className='qd-booking-notes'>{booking.notes}</p>}
                </div>
              </div>
            ) : (
              <p className='qd-text' style={{fontStyle:'italic'}}>No session booked yet</p>
            )}
          </div>

          <div className='qd-section qd-meta'>
            <span>Submitted: {formatDate(quote.submitted_at)}</span>
            <span>ID: #{quote.id}</span>
          </div>

        </div>
      </div>
    </div>
  )
}